import { useLocation } from "react-router-dom";

import {
  BusinessRounded,
  HelpOutlineRounded,
  ManageAccountsRounded,
  MenuOpenRounded,
  MenuRounded,
} from "@mui/icons-material";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import { Button, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import MenuList from "@mui/material/MenuList";

import routes from "@constants/routes";

import useNavigateWithSearchParameters from "@hooks/useNavigateWithSearchParameters";

import { IUser } from "@interfaces/user.type";

import { openProductUpdateNotification } from "@services/popups/user.popups";
import UserService from "@services/user.service";

import { usePopupActions } from "@store/popupStore";
import { useUserStore } from "@store/userStore";

import theme from "@theme/index";

import AccountModal from "./AccountModal";
import SidebarItem from "./SidebarItem";
import sidebarStyles from "./sidebarStyles";

type NavItemType = {
  icon: JSX.Element;
  isComingSoon?: boolean;
  isSelected: boolean;
  label: string;
  onClick: () => void;
  type: "header" | "item";
};

interface ISidebarProperties {
  isExpanded: boolean;
  toggleExpanded: () => void;
}

export default function Sidebar({ isExpanded, toggleExpanded }: ISidebarProperties) {
  const navigate = useNavigateWithSearchParameters();
  const PopupActions = usePopupActions();
  const user = useUserStore();

  const { pathname } = useLocation();

  const openAccountModal = (userData: IUser) => {
    PopupActions.openModal({
      content: <AccountModal onSubmit={UserService.update} user={userData} />,
      title: "Account Settings",
    });
  };

  const navItems: NavItemType[] = [
    {
      icon: <></>,
      isSelected: false,
      label: "Account",
      onClick: () => void 0,
      type: "header",
    },
    {
      icon: <Groups2RoundedIcon />,
      isSelected: pathname.includes(routes.ORGANIZATION),
      label: "Organization",
      onClick: () => {
        navigate(routes.ORGANIZATION + "/" + user.organizationId);
      },
      type: "item",
    },
    {
      icon: <ManageAccountsRounded />,
      isSelected: false,
      label: "Settings",
      onClick: () => {
        openAccountModal(user);
      },
      type: "item",
    },
    {
      icon: <></>,
      isSelected: false,
      label: "Innovation Data",
      onClick: () => void 0,
      type: "header",
    },
    {
      icon: <BusinessRounded />,
      isSelected: pathname.includes(routes.EMBEDDING_SEARCH_COMPANIES),
      label: "Companies",
      onClick: () => {
        navigate(routes.EMBEDDING_SEARCH_COMPANIES);
      },
      type: "item",
    },
    // {
    //   icon: <EmojiObjectsRounded />,
    //   isComingSoon: true,
    //   isSelected: pathname.includes(routes.PATENTS),
    //   label: "Patents",
    //   onClick: () => {
    //     navigate(routes.PATENTS);
    //   },
    //   type: "item",
    // },
    // {
    //   icon: <MoveUpRounded />,
    //   isComingSoon: true,
    //   isSelected: pathname.includes(routes.TRANSACTIONS),
    //   label: "Transactions",
    //   onClick: () => {
    //     navigate(routes.TRANSACTIONS);
    //   },
    //   type: "item",
    // },
  ];

  const handleHelp = () => {
    window.open("https://scaleip.notion.site/Customer-Resources-f8e4a51af2354bc68603aaad9b3ba397", "_blank");
  };

  return (
    <Box
      sx={{
        ...sidebarStyles.root,
        transition: "width 0.3s ease-in-out",
        width: isExpanded ? 260 : 72,
      }}
    >
      <Box sx={sidebarStyles.sidebar}>
        <MenuList sx={sidebarStyles.menuList}>
          {navItems.map((item) =>
            item.type === "header" ? (
              <Typography
                key={item.label}
                sx={{
                  color: theme.palette.grey[400],
                  display: isExpanded ? "block" : "none",
                  fontSize: "12px",
                  fontWeight: "bold",
                  height: isExpanded ? "auto" : 0,
                  mb: 1,
                  mt: 2,
                  opacity: isExpanded ? 1 : 0,
                  overflow: "hidden",
                  pl: 1,
                  transition: "opacity 0.3s ease-in-out",
                }}
                variant="caption"
              >
                {item.label}
              </Typography>
            ) : (
              <SidebarItem
                icon={item.icon}
                isComingSoon={item.isComingSoon}
                isSelected={item.isSelected}
                isSidebarOpen={isExpanded}
                key={item.label}
                label={item.label}
                onClick={item.onClick}
              />
            ),
          )}
        </MenuList>
        <MenuList
          sx={{
            ...sidebarStyles.menuList,
            justifyContent: "flex-end",
            p: 0,
          }}
        >
          <Button
            onClick={() => {
              openProductUpdateNotification(user);
            }}
            size="small"
            sx={{
              borderRadius: "40px",
              fontSize: "12px",
              height: "24px",
              opacity: isExpanded ? 1 : 0,
              pointerEvents: isExpanded ? "auto" : "none",
              px: 0,
              transform: `translateY(${isExpanded ? 56 : 0}px)`,
              transition: "all 0.3s ease-in-out",
              width: "97px",
            }}
            variant="contained"
          >
            {" "}
            What's New?{" "}
          </Button>
          <SidebarItem
            icon={<HelpOutlineRounded />}
            isSelected={false}
            isSidebarOpen={isExpanded}
            label=""
            onClick={handleHelp}
            sx={{
              transform: `translateY(${isExpanded ? 56 : 0}px)`,
              transition: "transform 0.3s ease-in-out",
              width: "56px",
            }}
          />
          <SidebarItem
            icon={isExpanded ? <MenuOpenRounded /> : <MenuRounded />}
            isSelected={false}
            isSidebarOpen={isExpanded}
            label=""
            onClick={toggleExpanded}
            sx={{
              transform: `translateX(${isExpanded ? 188 : 0}px)`,
              transition: "transform 0.3s ease-in-out",
              width: "56px",
            }}
          />
        </MenuList>
      </Box>
    </Box>
  );
}

Sidebar.propTypes = {};
